import type { ListAuditQuery } from '@clockwise/clients/audit';
import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { auditApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { AuditEntryList } from './audit-entry-list';

const DEFAULT_PAGE_SIZE = 50;

/**
 * Roll-up audit read for a single parent (a customer's or employee's tab):
 * the parent's own history plus every child scoped to it. Shared by every
 * scope-based audit surface so the query key, endpoint, and page size stay in
 * one place. Returns the raw query so the host can read `data.meta.total` (e.g.
 * a tab-count badge); pair with {@link ScopedAuditPanel} for the list itself.
 */
export function useScopedAudit(opts: {
  scopeType: string;
  scopeId: string;
  enabled: boolean;
  pageSize?: number;
}) {
  const query = {
    scopeType: opts.scopeType,
    scopeId: opts.scopeId,
    limit: opts.pageSize ?? DEFAULT_PAGE_SIZE,
    offset: 0,
  } satisfies ListAuditQuery;
  return useQuery({
    queryKey: queryKeys.audit.forEntity(query),
    queryFn: () => auditApi.list(query),
    enabled: opts.enabled,
  });
}

/**
 * Loading / error / list rendering for a {@link useScopedAudit} query. `roll-up`
 * surfaces mix entity types, so the list always shows the per-row object-type
 * chip. Pass `resolveLabel` to translate `customFields.<uuid>` field keys.
 */
export function ScopedAuditPanel({
  query,
  resolveLabel,
}: {
  query: ReturnType<typeof useScopedAudit>;
  resolveLabel?: (field: string, fallback: string) => string;
}) {
  if (query.isLoading) return <Skeleton className="h-32 w-full" />;
  if (query.isError) {
    return (
      <p className="p-4 text-sm text-destructive">
        {query.error instanceof ApiError ? query.error.message : 'Failed to load audit log.'}
      </p>
    );
  }
  return (
    <AuditEntryList
      items={query.data?.data ?? []}
      total={query.data?.meta.total ?? 0}
      resolveLabel={resolveLabel}
      showObjectType
    />
  );
}
